export const queryWorkStatus = (task) => {
  if (task.queryReviewOutcome === "ChangesRequested") return "Changes requested";
  if (task.taskType === "QueryResponse" && /respondent/i.test(task.assignedRole || "")) {
    if (task.queryResponseState === "ChangesRequested") return "Changes requested";
    if (task.queryResponseState === "AwaitingReview") return "Awaiting review";
  }
  return null;
};
// Workflow ownership is enforced by review-tasks/mine; only Active steps are actionable.
export const needsAction = (task) => String(task.workflowStatus || "").toLowerCase() === "active";
export const isWaiting = (task) => String(task.workflowStatus || "").toLowerCase() === "pending";
const dueDay = (task) => {
  const value = task.dueDate ? new Date(task.dueDate).setHours(0, 0, 0, 0) : NaN;
  return Number.isFinite(value) ? value : Infinity;
};
export const actionLabel = (task) => task.taskType === "QueryResponse" && task.queryResponseState === "ChangesRequested" ? "Revise response" : ({
  QueryResponse: /raiser/i.test(task.assignedRole || "") ? "Review response" : "Respond to query",
  QueryResponseReview: "Review draft",
  QueryResponseApproval: "Approve response",
  FindingResponse: "Respond to finding",
  ChecklistReview: "Continue review",
  ChecklistSignOff: "Sign off checklist",
  FinalSignOff: "Final sign-off",
}[task.taskType] || "Open assignment");
export const priorityReason = (task, today = new Date().setHours(0, 0, 0, 0)) => {
  if (task.taskType === "QueryResponse" && task.queryResponseState === "ChangesRequested") return "Partner requested changes";
  const due = dueDay(task);
  if (due < today) return `Overdue by ${Math.round((today - due) / 86400000)} day(s)`;
  if (due === today) return "Due today";
  if (due <= today + 7 * 86400000) return `Due ${new Date(due).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  if (/Approval|SignOff/.test(task.taskType)) return "Awaiting your approval";
  if (task.taskType === "QueryResponseReview") return "Draft ready for your review";
  return "Ready for your action";
};
export const priorityFirst = (a, b) => {
  const today = new Date().setHours(0, 0, 0, 0);
  const rank = (task) => dueDay(task) < today ? 0 : dueDay(task) <= today + 7 * 86400000 ? 1 : /Approval|SignOff|ResponseReview/.test(task.taskType) ? 2 : 3;
  return rank(a) - rank(b) || dueDay(a) - dueDay(b) || Number(b.priority || 0) - Number(a.priority || 0) || String(a.id).localeCompare(String(b.id));
};
export const taskPath = (task) => task.queryId
  ? `/review-tasks?inspectionId=${task.inspectionId}&queryId=${task.queryId}&taskId=${task.id}`
  : task.taskType === "ChecklistReview"
  ? `/inspectionreview/${task.inspectionId}/checklist?assignedReview=1`
  : `/inspectionreview/${task.inspectionId}?workflowTaskId=${task.id}`;

const latestDate = (values) =>
  values.reduce(
    (latest, value) =>
      (new Date(value).getTime() || 0) > (new Date(latest).getTime() || 0)
        ? value
        : latest,
    null
  );

const earliestDate = (values) =>
  values.reduce((earliest, value) => {
    if (!value) return earliest;
    return !earliest || new Date(value).getTime() < new Date(earliest).getTime()
      ? value
      : earliest;
  }, null);

export const consolidateChecklistReviews = (tasks) => {
  const grouped = new Map();
  const otherTasks = [];
  tasks.forEach((task) => {
    if (task.taskType !== "ChecklistReview") {
      otherTasks.push(task);
      return;
    }
    const key = String(task.inspectionId);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(task);
  });
  const reviews = Array.from(grouped.values(), (reviewTasks) => {
    const first = reviewTasks[0];
    const statuses = reviewTasks.map((task) =>
      String(task.workflowStatus || "").toLowerCase()
    );
    const workflowStatus = statuses.includes("active")
      ? "active"
      : statuses.includes("pending")
      ? "pending"
      : statuses.every((status) => status === "completed")
      ? "completed"
      : first.workflowStatus || "pending";
    const checklistIds = [
      ...new Set(
        reviewTasks.map((task) => Number(task.checklistId)).filter(Boolean)
      ),
    ];
    return {
      ...first,
      id: `inspection-review-${first.inspectionId}`,
      taskName: `Complete ${first.inspectionName || "inspection review"}`,
      workflowStatus,
      workflowTaskIds: reviewTasks
        .map((task) => Number(task.id))
        .filter(Boolean),
      checklistIds,
      checklistCount: checklistIds.length || reviewTasks.length,
      dueDate: earliestDate(reviewTasks.filter((task) => workflowStatus !== "active" || needsAction(task)).map((task) => task.dueDate)),
      updatedOn: latestDate(reviewTasks.map((task) => task.updatedOn)),
      createdOn: latestDate(reviewTasks.map((task) => task.createdOn)),
      isUnread: reviewTasks.some((task) => task.isUnread),
      priority: Math.max(
        ...reviewTasks.map((task) => Number(task.priority) || 0)
      ),
      isConsolidatedReview: true,
    };
  });
  return [...reviews, ...otherTasks];
};

